import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, CheckCircle, XCircle, Loader2, Languages } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * QuizView - Quiz/exam view component
 * Displays questions, handles answers, shows results
 */
function QuizView({ 
  questions, currentIndex, score, selectedAnswer, showExplanation, 
  finished, loading, quizType, onAnswer, onNext, onClose, language = 'he'
}) {
  const currentQuestion = questions[currentIndex];
  const percentage = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
  const passed = percentage >= 60;
  const isEn = language === 'en';

  // Track translations per (question index, language) - "he" and "ar" cached independently
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState(false);

  // Which language is currently displayed: null = original, "he" or "ar" = translated
  const [displayLang, setDisplayLang] = useState(null);
  useEffect(() => {
    setDisplayLang(null);
  }, [currentIndex]);

  const handleTranslate = async (targetLang) => {
    if (!currentQuestion?.id) return;
    // If we already have it cached — just show it
    if (translations[`${currentIndex}:${targetLang}`]) {
      setDisplayLang(targetLang);
      return;
    }
    setTranslating(true);
    try {
      const r = await axios.post(`${API_BASE}/api/ai/translate-question`, {
        question_id: currentQuestion.id,
        target_lang: targetLang,
      });
      setTranslations(prev => ({
        ...prev,
        [`${currentIndex}:${targetLang}`]: {
          text: r.data.text,
          options: r.data.options,
          explanation: r.data.explanation,
        }
      }));
      setDisplayLang(targetLang);
    } catch (e) {
      console.error('Translation failed', e);
      alert('שגיאה בתרגום. נסה שוב עוד רגע.');
    } finally {
      setTranslating(false);
    }
  };

  // Get text in selected language with fallback (+ AI-translated override when toggled)
  const getQ = (q) => {
    if (!q) return { text: '', options: [], explanation: '' };
    // If a translation is active and cached for this question — use it
    const t = displayLang ? translations[`${currentIndex}:${displayLang}`] : null;
    if (t) {
      return {
        text: t.text,
        options: t.options?.length ? t.options : q.options,
        explanation: t.explanation || q.explanation,
      };
    }
    return {
      text: isEn ? (q.text_en || q.text) : (q.text_he || q.text),
      options: isEn ? (q.options_en?.length ? q.options_en : q.options) : (q.options_he?.length ? q.options_he : q.options),
      explanation: isEn ? (q.explanation_en || q.explanation) : (q.explanation_he || q.explanation),
    };
  };

  // Only show the "Translate to Hebrew" button when the current question looks English
  // (i.e., original text is mostly ASCII / language flag is 'en' / text_en matches text).
  const looksEnglish = (q) => {
    const t = q?.text || '';
    if (!t) return false;
    // Must have a real DB UUID id — otherwise translate API cannot find it
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!q?.id || !uuidRegex.test(q.id)) return false;
    if (q?.language === 'en') return true;
    if (q?.text_en && q.text_en === t && !q.text_he) return true;
    // Simple heuristic: >50% of characters are ASCII letters
    const ascii = (t.match(/[A-Za-z]/g) || []).length;
    return ascii / t.length > 0.5;
  };

  const quizTypeLabel = {
    'governmental': 'מבחן מסכם',
    'quick': 'מבחן בזק',
    'course': 'מבחן קורס',
    'bar-exam': 'בחינת לשכה',
    'chapter': 'מבחן פרק',
    'gov-exam': 'מבחן מסכם'
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8 flex flex-col" dir="rtl">
      <div className="max-w-3xl mx-auto w-full flex-1 relative">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-0 left-0 z-10 p-2 font-black text-slate-400 flex items-center gap-2 hover:text-slate-900 transition-colors"
          data-testid="close-quiz-button"
        >
          <X className="w-5 h-5" />
          <span className="hidden sm:inline">חזרה לקורס</span>
        </button>

        {/* Quiz Type Label */}
        <div className="text-center mb-6">
          <span className="inline-block bg-teal-100 text-teal-800 text-xs font-bold px-4 py-1 rounded-full">
            {quizTypeLabel[quizType] || 'מבחן'}
          </span>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-center animate-slide-up">
            <div className="text-8xl mb-8 animate-float">🩺</div>
            <h2 className="text-3xl font-black text-slate-900 mb-4">מכין את המבחן...</h2>
            <p className="text-slate-400 font-medium">המבחן נבנה בהתאם לסילבוס 2026</p>
            <div className="spinner mt-8"></div>
          </div>
        )}

        {/* Quiz Finished */}
        {finished && !loading && (
          <div className="bg-white mt-8 p-10 sm:p-16 rounded-3xl shadow-2xl text-center border animate-zoom-in">
            <div className="text-8xl mb-6">{passed ? '🎉' : '📚'}</div>
            <h2 className="text-4xl font-black text-slate-900 mb-4" data-testid="quiz-result-title">
              {passed ? 'עברת את המבחן!' : 'זמן לתרגול נוסף'}
            </h2>
            <div className={`text-6xl font-black mb-6 ${passed ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="quiz-score">
              {percentage}%
            </div>
            <p className="text-slate-500 mb-8 font-medium">
              ענית נכון על {score} מתוך {questions.length} שאלות
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={onClose}
                className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-lg active:scale-95 transition-all"
                data-testid="back-to-course-button"
              >
                חזרה לקורס
              </button>
            </div>
          </div>
        )}

        {/* Active Quiz */}
        {!loading && !finished && currentQuestion && (
          <div className="space-y-6 animate-slide-up">
            {/* Progress */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border flex justify-between items-center">
              <span className="font-black text-slate-900">
                שאלה {currentIndex + 1} מתוך {questions.length}
              </span>
              <div className="w-32 sm:w-48 h-3 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
                  style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Question Card */}
            <div className="bg-white p-8 sm:p-12 rounded-3xl shadow-xl border">
              {/* Translate buttons: only for English questions */}
              {looksEnglish(currentQuestion) && (
                <div className="flex justify-end -mt-2 mb-4 gap-2 flex-wrap">
                  {displayLang && (
                    <button
                      onClick={() => setDisplayLang(null)}
                      className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full border bg-white text-slate-600 border-slate-200 hover:border-slate-400 transition-all"
                      data-testid="show-original-btn"
                    >
                      🇺🇸 הצג באנגלית
                    </button>
                  )}
                  <button
                    onClick={() => handleTranslate('he')}
                    disabled={translating}
                    className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${
                      displayLang === 'he'
                        ? 'bg-teal-100 text-teal-800 border-teal-300 hover:bg-teal-200'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700'
                    } ${translating ? 'opacity-60 cursor-wait' : ''}`}
                    data-testid="translate-question-he-btn"
                  >
                    {translating && displayLang !== 'ar' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Languages className="w-4 h-4" />
                    )}
                    🇮🇱 עברית
                  </button>
                  <button
                    onClick={() => handleTranslate('ar')}
                    disabled={translating}
                    className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${
                      displayLang === 'ar'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:text-emerald-700'
                    } ${translating ? 'opacity-60 cursor-wait' : ''}`}
                    data-testid="translate-question-ar-btn"
                  >
                    {translating && displayLang !== 'he' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Languages className="w-4 h-4" />
                    )}
                    🇸🇦 عربي
                  </button>
                </div>
              )}
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-8 leading-relaxed" dir={displayLang ? 'rtl' : (isEn ? 'ltr' : 'rtl')} data-testid="question-text">
                {getQ(currentQuestion).text}
              </h3>
              {/* Show other language as subtitle */}
              {isEn && currentQuestion?.text_he && currentQuestion?.text_en && (
                <p className="text-sm text-slate-400 mb-6 -mt-4" dir="rtl">{currentQuestion.text_he}</p>
              )}
              {!isEn && currentQuestion?.text_en && (
                <p className="text-sm text-slate-400 mb-6 -mt-4" dir="ltr">{currentQuestion.text_en}</p>
              )}

              {/* Options */}
              <div className="space-y-3">
                {getQ(currentQuestion).options?.map((option, i) => {
                  let optionClass = 'quiz-option quiz-option-default';
                  if (selectedAnswer !== null) {
                    if (i === currentQuestion.correct_answer) {
                      optionClass = 'quiz-option quiz-option-correct';
                    } else if (i === selectedAnswer) {
                      optionClass = 'quiz-option quiz-option-incorrect';
                    }
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => onAnswer(i)}
                      className={optionClass}
                      disabled={selectedAnswer !== null}
                      data-testid={`option-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        {selectedAnswer !== null && i === currentQuestion.correct_answer && (
                          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        )}
                        {selectedAnswer !== null && i === selectedAnswer && i !== currentQuestion.correct_answer && (
                          <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0" />
                        )}
                        <span className="font-bold">{option}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              {showExplanation && (
                <div className="mt-8 p-6 sm:p-8 bg-slate-900 text-white rounded-2xl animate-slide-up">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">💡</span>
                    <span className="font-black text-teal-400 uppercase tracking-widest text-xs">{isEn ? 'EXPLANATION' : 'רציונל קליני'}</span>
                  </div>
                  <p className="text-base sm:text-lg font-medium leading-relaxed mb-6" dir={isEn ? 'ltr' : 'rtl'} data-testid="explanation-text">
                    {getQ(currentQuestion).explanation}
                  </p>
                  <button 
                    onClick={onNext}
                    className="w-full bg-teal-500 hover:bg-teal-400 py-4 rounded-xl font-black text-slate-900 text-lg transition-all active:scale-95"
                    data-testid="next-question-button"
                  >
                    {currentIndex + 1 < questions.length ? (isEn ? 'Next Question' : 'לשאלה הבאה') : (isEn ? 'Finish Exam' : 'סיום המבחן')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizView;
